import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { useProperty } from "@/context/PropertyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, Download, AlertTriangle, Shirt, Building2, RefreshCw, ClipboardList, PackageCheck, Users, CheckCircle2, Search, FileText } from "lucide-react";
import { toast } from "sonner";
import { logger } from "@/lib/logger";
import UniformIssuanceLog from "@/components/uniform/UniformIssuanceLog";

export default function UniformFulfilment() {
  const { selectedId } = useProperty();
  const [tab, setTab] = useState("order");

  return (
    <div className="space-y-5 max-w-6xl">
      <div>
        <span className="label-overline">Setup</span>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter mt-1">Uniform Operations</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
          Plan vendor orders from employee sizes and track what's actually been handed out.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="bg-white border border-zinc-200 h-10 p-1" data-testid="uniform-tabs">
          <TabsTrigger value="order" className="data-[state=active]:bg-zinc-950 data-[state=active]:text-white px-4 py-1.5" data-testid="uniform-tab-order">
            <ClipboardList className="w-3.5 h-3.5 mr-1.5" /> Vendor Order Sheet
          </TabsTrigger>
          <TabsTrigger value="issuance" className="data-[state=active]:bg-zinc-950 data-[state=active]:text-white px-4 py-1.5" data-testid="uniform-tab-issuance">
            <PackageCheck className="w-3.5 h-3.5 mr-1.5" /> Issuance Log
          </TabsTrigger>
          <TabsTrigger value="submissions" className="data-[state=active]:bg-zinc-950 data-[state=active]:text-white px-4 py-1.5" data-testid="uniform-tab-submissions">
            <Users className="w-3.5 h-3.5 mr-1.5" /> Submission Status
          </TabsTrigger>
          <TabsTrigger value="bydept" className="data-[state=active]:bg-zinc-950 data-[state=active]:text-white px-4 py-1.5" data-testid="uniform-tab-bydept">
            <Building2 className="w-3.5 h-3.5 mr-1.5" /> By Department
          </TabsTrigger>
        </TabsList>

        <TabsContent value="order" className="mt-5">
          <OrderSheet propertyId={selectedId} />
        </TabsContent>
        <TabsContent value="issuance" className="mt-5">
          <UniformIssuanceLog propertyId={selectedId} />
        </TabsContent>
        <TabsContent value="submissions" className="mt-5">
          <SubmissionStatus propertyId={selectedId} />
        </TabsContent>
        <TabsContent value="bydept" className="mt-5">
          <ByDepartment propertyId={selectedId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function OrderSheet({ propertyId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = propertyId ? `/uniform-fulfilment?property_id=${propertyId}` : "/uniform-fulfilment";
      const { data: d } = await api.get(url);
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const downloadXlsx = async () => {
    setDownloading(true);
    try {
      const url = propertyId
        ? `/uniform-fulfilment/export?property_id=${propertyId}`
        : "/uniform-fulfilment/export";
      const resp = await api.get(url, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = `uniform_fulfilment_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      toast.success("Exported");
    } catch (e) {
      logger.error("fulfilment export", e);
      toast.error(e.response?.data?.detail || "Export failed");
    } finally {
      setDownloading(false);
    }
  };

  const totals = useMemo(() => {
    if (!data) return { properties: 0, employees: 0, missing: 0 };
    const properties = data.properties.length;
    const employees = data.properties.reduce((a, p) => a + p.total_employees, 0);
    const missing = data.properties.reduce((a, p) => a + p.missing, 0);
    return { properties, employees, missing };
  }, [data]);

  if (loading) return <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="text-sm text-zinc-500 max-w-2xl">
          How many of each size you need to order, grouped by property. Hand the Excel straight to your uniform vendor.
        </p>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="uniform-fulfilment-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> Refresh
          </Button>
          <Button onClick={downloadXlsx} disabled={downloading} className="bg-zinc-950 hover:bg-zinc-800 h-10" data-testid="uniform-fulfilment-download">
            {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Download Excel
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Stat label="Properties" value={totals.properties} />
        <Stat label="Employees" value={totals.employees} />
        <Stat label="Sizes pending" value={totals.missing} accent={totals.missing > 0 ? "amber" : "neutral"} />
      </div>

      {data.properties.length === 0 && (
        <div className="bg-white border border-dashed border-zinc-200 rounded-md py-16 text-center text-zinc-400">
          No employees in scope.
        </div>
      )}

      {data.properties.map((p) => (
        <section key={p.property_id} className="bg-white border border-zinc-200 rounded-md overflow-hidden" data-testid={`uniform-fulfilment-property-${p.property_id}`}>
          <div className="px-4 py-3 bg-zinc-50 border-b border-zinc-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-zinc-500" />
              <h2 className="font-display text-lg font-bold tracking-tight">{p.property_name}</h2>
            </div>
            <div className="flex items-center gap-3 text-[11px]">
              <span className="text-zinc-500">{p.total_employees} employees</span>
              {p.missing > 0 && (
                <span className="inline-flex items-center gap-1 font-bold uppercase tracking-widest text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                  <AlertTriangle className="w-3 h-3" /> {p.missing} sizes pending
                </span>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-100">
            {p.categories.map((cat) => (
              <div key={cat.key} className="bg-white p-4" data-testid={`uniform-fulfilment-category-${p.property_id}-${cat.key}`}>
                <div className="flex items-center gap-1.5 label-overline mb-2">
                  <Shirt className="w-3.5 h-3.5" /> {cat.label}
                </div>
                {cat.sizes.length === 0 ? (
                  <div className="text-zinc-400 text-xs italic">No sizes captured yet.</div>
                ) : (
                  <ul className="space-y-1">
                    {cat.sizes.map((s) => (
                      <li key={s.size} className="flex items-baseline justify-between text-sm">
                        <span className="font-mono text-zinc-700" title={s.employee_names.join(", ")}>{s.size}</span>
                        <span className="font-bold tabular-nums">{s.count}</span>
                      </li>
                    ))}
                  </ul>
                )}
                {cat.unset_count > 0 && (
                  <div className="mt-2 text-[10px] text-amber-700" title={cat.unset_employees.join(", ")}>
                    <AlertTriangle className="w-3 h-3 inline" /> {cat.unset_count} not set
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>
      ))}

      <div className="text-[11px] text-zinc-400 text-right">
        Generated {new Date(data.generated_at).toLocaleString()}
      </div>
    </div>
  );
}

function SubmissionStatus({ propertyId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all | complete | partial | none
  const [deptFilter, setDeptFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = propertyId ? `/uniform-fulfilment/submissions?property_id=${propertyId}` : "/uniform-fulfilment/submissions";
      const { data: d } = await api.get(url);
      setData(d);
      setSelected(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load submission status");
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  const departments = useMemo(() => {
    if (!data) return [];
    return Array.from(new Set(data.employees.map((e) => e.department || "Unassigned"))).sort();
  }, [data]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return data.employees.filter((e) => {
      if (filter !== "all" && e.status !== filter) return false;
      if (deptFilter !== "all" && (e.department || "Unassigned") !== deptFilter) return false;
      if (!q) return true;
      return [e.name, e.employee_id, e.department].some((v) => (v || "").toLowerCase().includes(q));
    });
  }, [data, filter, deptFilter, query]);

  const toggle = (id) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allFilteredSelected = filtered.length > 0 && filtered.every((e) => selected.has(e.user_id));
  const toggleAll = () => setSelected((prev) => {
    const next = new Set(prev);
    if (allFilteredSelected) filtered.forEach((e) => next.delete(e.user_id));
    else filtered.forEach((e) => next.add(e.user_id));
    return next;
  });

  const exportPdf = async () => {
    const ids = filtered.filter((e) => selected.has(e.user_id)).map((e) => e.user_id);
    // If nothing selected, export everyone currently filtered.
    const body = {
      property_id: propertyId || undefined,
      user_ids: ids.length ? ids : filtered.map((e) => e.user_id),
    };
    if (!body.user_ids.length) { toast.error("No team members to export"); return; }
    setExporting(true);
    try {
      const resp = await api.post("/uniform-fulfilment/submissions/export-pdf", body, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `uniform_submissions_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Exported ${body.user_ids.length} team member(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "PDF export failed");
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (!data) return null;

  const s = data.summary;
  const cats = data.category_labels;
  const selectedCount = filtered.filter((e) => selected.has(e.user_id)).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <p className="text-sm text-zinc-500 max-w-2xl">
          Who has filled in their uniform sizes and who hasn't. Pending employees are listed first so you can chase them.
        </p>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="uniform-submissions-refresh">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* Summary cards (clickable to filter) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <button type="button" onClick={() => setFilter("all")} className="text-left" data-testid="uniform-sub-filter-all">
          <Stat label="Total employees" value={s.total} accent={filter === "all" ? "active" : "neutral"} />
        </button>
        <button type="button" onClick={() => setFilter("complete")} className="text-left" data-testid="uniform-sub-filter-complete">
          <Stat label="Fully submitted" value={s.complete} accent={filter === "complete" ? "active" : "emerald"} />
        </button>
        <button type="button" onClick={() => setFilter("partial")} className="text-left" data-testid="uniform-sub-filter-partial">
          <Stat label="Partially filled" value={s.partial} accent={filter === "partial" ? "active" : "amber"} />
        </button>
        <button type="button" onClick={() => setFilter("none")} className="text-left" data-testid="uniform-sub-filter-none">
          <Stat label="Not submitted" value={s.none} accent={filter === "none" ? "active" : (s.none > 0 ? "red" : "neutral")} />
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-56">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-zinc-400" />
            <Input placeholder="Search name, ID, department…" value={query} onChange={(e) => setQuery(e.target.value)} className="h-9 pl-8" data-testid="uniform-sub-search" />
          </div>
          <Select value={deptFilter} onValueChange={setDeptFilter}>
            <SelectTrigger className="h-9 w-48" data-testid="uniform-sub-dept-filter">
              <SelectValue placeholder="All departments" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All departments</SelectItem>
              {departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={exportPdf} disabled={exporting || filtered.length === 0} className="bg-zinc-950 hover:bg-zinc-800" data-testid="uniform-sub-export-pdf">
          {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FileText className="w-4 h-4 mr-1.5" />}
          {selectedCount > 0 ? `Export ${selectedCount} as PDF` : "Export PDF"}
        </Button>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto" data-testid="uniform-submissions-table">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
              <th className="py-2.5 px-3 w-10">
                <Checkbox checked={allFilteredSelected} onCheckedChange={toggleAll} aria-label="Select all" data-testid="uniform-sub-select-all" />
              </th>
              <th className="py-2.5 px-3">Employee</th>
              <th className="py-2.5 px-3">Department</th>
              {cats.map((c) => <th key={c.key} className="py-2.5 px-3 text-center">{c.label}</th>)}
              <th className="py-2.5 px-3 text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={cats.length + 4} className="py-10 text-center text-zinc-400">No employees match.</td></tr>
            )}
            {filtered.map((e) => (
              <tr key={e.user_id} className="border-b border-zinc-100 last:border-0 hover:bg-zinc-50" data-testid={`uniform-sub-row-${e.user_id}`} data-status={e.status}>
                <td className="py-2.5 px-3">
                  <Checkbox checked={selected.has(e.user_id)} onCheckedChange={() => toggle(e.user_id)} aria-label={`Select ${e.name}`} data-testid={`uniform-sub-check-${e.user_id}`} />
                </td>
                <td className="py-2.5 px-3">
                  <div className="font-medium">{e.name}</div>
                  <div className="text-[11px] text-zinc-500 font-mono">{e.employee_id || "—"} · {(e.role || "").replace(/_/g, " ")}</div>
                </td>
                <td className="py-2.5 px-3 text-zinc-600">{e.department || "—"}</td>
                {cats.map((c) => {
                  const v = e.sizes[c.key];
                  return (
                    <td key={c.key} className="py-2.5 px-3 text-center">
                      {v ? <span className="font-mono font-semibold text-zinc-800">{v}</span> : <span className="text-zinc-300">—</span>}
                    </td>
                  );
                })}
                <td className="py-2.5 px-3 text-center"><StatusPill status={e.status} count={e.submitted_count} total={e.total_categories} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="text-[11px] text-zinc-400 text-right">
        Generated {new Date(data.generated_at).toLocaleString()}
      </div>
    </div>
  );
}

function ByDepartment({ propertyId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = propertyId ? `/uniform-fulfilment/by-department?property_id=${propertyId}` : "/uniform-fulfilment/by-department";
      const { data: d } = await api.get(url);
      setData(d);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load department requirements");
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="uniform-bydept">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <p className="text-sm text-zinc-500 max-w-2xl">
          Uniform requirement broken down by department — the size mix to order for each team.
        </p>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="uniform-bydept-refresh">
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {data.departments.length === 0 ? (
        <div className="bg-white border border-dashed border-zinc-200 rounded-md py-16 text-center text-zinc-400">No departments in scope.</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {data.departments.map((dep) => (
            <section key={dep.department} className="bg-white border border-zinc-200 rounded-md p-4" data-testid={`uniform-dept-${dep.department}`}>
              <div className="flex items-center justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-zinc-500" />
                  <h3 className="font-display text-lg font-bold tracking-tight">{dep.department}</h3>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <span className="bg-zinc-100 text-zinc-700 rounded px-2 py-0.5">{dep.total_employees} staff</span>
                  {dep.missing > 0 && <span className="bg-red-50 text-red-700 rounded px-2 py-0.5">{dep.missing} sizes pending</span>}
                </div>
              </div>
              <div className="space-y-2.5">
                {dep.categories.map((cat) => (
                  <div key={cat.key} className="flex items-start gap-3">
                    <div className="w-28 shrink-0 text-xs font-semibold text-zinc-600 pt-1">{cat.label}</div>
                    <div className="flex flex-wrap gap-1.5 flex-1">
                      {cat.sizes.length === 0 ? (
                        <span className="text-xs text-zinc-300">No sizes submitted</span>
                      ) : (
                        cat.sizes.map((sz) => (
                          <span key={sz.size} className="inline-flex items-center gap-1 text-xs bg-zinc-100 rounded px-2 py-0.5" data-testid={`uniform-dept-${dep.department}-${cat.key}-${sz.size}`}>
                            <span className="font-mono font-semibold">{sz.size}</span>
                            <span className="text-zinc-400">×</span>
                            <span className="font-bold">{sz.count}</span>
                          </span>
                        ))
                      )}
                      {cat.unset_count > 0 && (
                        <span className="text-[11px] text-red-500 self-center">+{cat.unset_count} pending</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      <div className="text-[11px] text-zinc-400 text-right">
        Generated {new Date(data.generated_at).toLocaleString()}
      </div>
    </div>
  );
}

function StatusPill({ status, count, total }) {
  if (status === "complete") {
    return <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-200 rounded px-1.5 py-0.5"><CheckCircle2 className="w-3 h-3" /> Submitted</span>;
  }
  if (status === "partial") {
    return <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-amber-50 text-amber-700 border border-amber-200 rounded px-1.5 py-0.5"><AlertTriangle className="w-3 h-3" /> {count}/{total}</span>;
  }
  return <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-red-50 text-red-700 border border-red-200 rounded px-1.5 py-0.5">Pending</span>;
}

function Stat({ label, value, accent }) {
  const palette = {
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    red: "bg-red-50 border-red-200 text-red-900",
    active: "bg-zinc-950 border-zinc-950 text-white",
    neutral: "bg-white",
  };
  return (
    <div className={`border border-zinc-200 rounded-md p-3 transition-colors ${palette[accent || "neutral"] || palette.neutral}`}>
      <div className={`text-[10px] uppercase tracking-widest ${accent === "active" ? "text-zinc-300" : "text-zinc-500"}`}>{label}</div>
      <div className="text-2xl font-bold font-display mt-0.5">{value}</div>
    </div>
  );
}
